/**
 * Chainlink Data Feed configuration
 * Contains price feed addresses for all supported chains and tokens
 */

import { CHAIN_IDS, type ChainId } from "./contracts";

/**
 * Chainlink Aggregator V3 Interface ABI (minimal)
 * Only includes functions needed for price reading
 */
export const AGGREGATOR_V3_ABI = [
  {
    inputs: [],
    name: "decimals",
    outputs: [{ internalType: "uint8", name: "", type: "uint8" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "description",
    outputs: [{ internalType: "string", name: "", type: "string" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "latestRoundData",
    outputs: [
      { internalType: "uint80", name: "roundId", type: "uint80" },
      { internalType: "int256", name: "answer", type: "int256" },
      { internalType: "uint256", name: "startedAt", type: "uint256" },
      { internalType: "uint256", name: "updatedAt", type: "uint256" },
      { internalType: "uint80", name: "answeredInRound", type: "uint80" },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const;

/**
 * Price feed configuration
 */
export interface PriceFeedConfig {
  /** Contract address of the Chainlink price feed */
  address: string;
  /** Human-readable description (e.g., "ETH / USD") */
  description: string;
  /** Decimals returned by the feed (usually 8 for USD pairs) */
  decimals: number;
  /** Heartbeat interval in seconds - max time between updates */
  heartbeat: number;
}

/**
 * L2 Sequencer Uptime Feed addresses
 * Used to check if L2 sequencer is up before trusting price data
 */
export const SEQUENCER_UPTIME_FEEDS: Partial<Record<ChainId, string>> = {
  [CHAIN_IDS.BASE]: "0xBCF85224fc0756B9Fa45aA7892530B47e10b6433",
  [CHAIN_IDS.OPTIMISM]: "0x371EAD81c9102C9BF4874A9075FFFf170F2Ee389",
  [CHAIN_IDS.ARBITRUM]: "0xFdB631F5EE196F0ed6FAa767959853A9F217697D",
};

/** Creates feed entries where wrapped token shares native token's feed */
function withWrapped(
  native: string,
  wrapped: string,
  config: PriceFeedConfig,
): Record<string, PriceFeedConfig> {
  return { [native]: config, [wrapped]: config };
}

const STABLECOIN_HEARTBEAT = 86400; // 24 hours

/**
 * Builds a `PriceFeedConfig` entry for a USD-pegged stablecoin using the standard 24-hour heartbeat.
 * @param address - On-chain Chainlink aggregator address for the feed.
 * @param symbol - Token symbol used in the feed description (e.g. `USDC`).
 * @returns The price feed configuration.
 */
function stablecoinFeed(address: string, symbol: string): PriceFeedConfig {
  return {
    address,
    description: `${symbol} / USD`,
    decimals: 8,
    heartbeat: STABLECOIN_HEARTBEAT,
  };
}

/**
 * Chainlink price feed addresses by chain
 * Maps token symbols to their USD price feed addresses
 */
export const CHAINLINK_FEEDS: Partial<
  Record<ChainId, Record<string, PriceFeedConfig>>
> = {
  // Base Mainnet
  [CHAIN_IDS.BASE]: {
    ...withWrapped("ETH", "WETH", {
      address: "0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 1200, // 20 minutes
    }),
    USDC: stablecoinFeed("0x7e860098F58bBFC8648a4311b374B1D669a2bc6B", "USDC"),
    USDT: stablecoinFeed("0xf19d560eB8d2ADf07BD6D13ed03e1D11215721F9", "USDT"),
    DAI: stablecoinFeed("0x591e79239a7d679378eC8c847e5038150364C78F", "DAI"),
  },

  // Optimism Mainnet
  [CHAIN_IDS.OPTIMISM]: {
    ...withWrapped("ETH", "WETH", {
      address: "0x13e3Ee699D1909E989722E753853AE30b17e08c5",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 1200,
    }),
    USDC: stablecoinFeed("0x16a9FA2FDa030272Ce99B29CF780dFA30361E0f3", "USDC"),
    USDT: stablecoinFeed("0xECef79E109e997bCA29c1c0897ec9d7b03647F5E", "USDT"),
    DAI: stablecoinFeed("0x8dBa75e83DA73cc766A7e5a0ee71F656BAb470d6", "DAI"),
    OP: {
      address: "0x0D276FC14719f9292D5C1eA2198673d1f4269246",
      description: "OP / USD",
      decimals: 8,
      heartbeat: 1200,
    },
  },

  // Ethereum Mainnet
  [CHAIN_IDS.ETHEREUM]: {
    ...withWrapped("ETH", "WETH", {
      address: "0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 3600,
    }),
    USDC: stablecoinFeed("0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6", "USDC"),
    USDT: stablecoinFeed("0x3E7d1eAB13ad0104d2750B8863b489D65364e32D", "USDT"),
    DAI: stablecoinFeed("0xAed0c38402a5d19df6E4c03F4E2DceD6e29c1ee9", "DAI"),
  },

  // Arbitrum Mainnet
  [CHAIN_IDS.ARBITRUM]: {
    ...withWrapped("ETH", "WETH", {
      address: "0x639Fe6ab55C921f74e7fac1ee960C0B6293ba612",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 1200,
    }),
    USDC: stablecoinFeed("0x50834F3163758fcC1Df9973b6e91f0F0F0434aD3", "USDC"),
    USDT: stablecoinFeed("0x3f3f5dF88dC9F13eac63DF89EC16ef6e7E25DdE7", "USDT"),
    DAI: stablecoinFeed("0xc5C8E77B397E531B8EC06BFb0048328B30E9eCfB", "DAI"),
    ARB: {
      address: "0xb2A824043730FE05F3DA2efaFa1CBbe83fa548D6",
      description: "ARB / USD",
      decimals: 8,
      heartbeat: 1200,
    },
  },

  // Polygon Mainnet
  [CHAIN_IDS.POLYGON]: {
    POL: {
      address: "0xAB594600376Ec9fD91F8e8dC09E7c8B8b1583DAF",
      description: "MATIC / USD",
      decimals: 8,
      heartbeat: 27,
    },
    ...withWrapped("ETH", "WETH", {
      address: "0xF9680D99D6C9589e2a93a78A04A279e509205945",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 27,
    }),
    USDC: stablecoinFeed("0xfE4A8cc5b5B2366C1B58Bea3858e81843583ee2e", "USDC"),
    USDT: stablecoinFeed("0x0A6513e40db6EB1b165869B680c55757bC9ED40e", "USDT"),
    DAI: stablecoinFeed("0x4746DeC9e833A82EC7C2C1245845D6E5533a6992", "DAI"),
  },

  // Avalanche Mainnet
  [CHAIN_IDS.AVALANCHE]: {
    AVAX: {
      address: "0x0A77230d17318075983913bC2145DB16C7366156",
      description: "AVAX / USD",
      decimals: 8,
      heartbeat: 120,
    },
    WAVAX: {
      address: "0x0A77230d17318075983913bC2145DB16C7366156",
      description: "AVAX / USD",
      decimals: 8,
      heartbeat: 120,
    },
    USDC: stablecoinFeed("0xF096872672F44d6EBA71458D74fe67F9a77a23B9", "USDC"),
    USDT: stablecoinFeed("0xEBE676ee90Fe1112671f19b6B7459bC678B67e8a", "USDT"),
    "DAI.e": stablecoinFeed(
      "0x51D7180edA2260cc4F6e4EebB82FEF5c3c2B8300",
      "DAI.e",
    ),
  },

  // Base Sepolia (Testnet) - Use mainnet feeds as proxy
  [CHAIN_IDS.BASE_SEPOLIA]: {
    ...withWrapped("ETH", "WETH", {
      address: "0x4aDC67696bA383F43DD60A9e78F2C97Fbbfc7cb1",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 3600,
    }),
  },

  // Optimism Sepolia (Testnet)
  [CHAIN_IDS.OPTIMISM_SEPOLIA]: {
    ...withWrapped("ETH", "WETH", {
      address: "0x61Ec26aA57019C486B10502285c5A3D4A4750AD7",
      description: "ETH / USD",
      decimals: 8,
      heartbeat: 3600,
    }),
  },
};

/**
 * Map CoinGecko token IDs to token symbols for Chainlink lookup
 */
export const COINGECKO_TO_SYMBOL: Record<string, string> = {
  ethereum: "ETH",
  "usd-coin": "USDC",
  tether: "USDT",
  dai: "DAI",
  optimism: "OP",
  "matic-network": "POL",
  "avalanche-2": "AVAX",
  arbitrum: "ARB",
};

/**
 * Get Chainlink feed config for a token on a specific chain
 * @param chainId - Chain ID
 * @param tokenSymbol - Token symbol (e.g., "ETH", "USDC")
 * @returns Feed configuration or undefined if not available
 */
export function getChainlinkFeed(
  chainId: ChainId | number,
  tokenSymbol: string,
): PriceFeedConfig | undefined {
  const chainFeeds = CHAINLINK_FEEDS[chainId as ChainId];
  if (!chainFeeds) return undefined;
  return chainFeeds[tokenSymbol.toUpperCase()];
}

/**
 * Check if a chain has a sequencer uptime feed (L2 chains)
 * @param chainId - Chain ID
 * @returns True if sequencer uptime feed is available
 */
export function hasSequencerFeed(chainId: ChainId | number): boolean {
  return chainId in SEQUENCER_UPTIME_FEEDS;
}

/**
 * Get sequencer uptime feed address for a chain
 * @param chainId - Chain ID
 * @returns Feed address or undefined
 */
export function getSequencerFeed(
  chainId: ChainId | number,
): string | undefined {
  return SEQUENCER_UPTIME_FEEDS[chainId as ChainId];
}
